import React, { ChangeEvent, FC, FormEvent, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import Alert from "../../../../components/Alert";
import Button from "../../../../components/Button";
import ButtonPrimary from "../../../../components/ButtonPrimary";
import Modal from "../../../../components/Modal";
import { ModalHeader } from "../../../../components/Modal/ModalHeader";
import { ModalContent } from "../../../../components/Modal/ModalContent";
import { ModalFooter } from "../../../../components/Modal/ModalFooter";
import { IChangePasswordProps } from "./types";
import FormGroup from "../../../../components/FormGroup";
import Label from "../../../../components/Label";
import PasswordInput from "../../../../components/PasswordInput";
import userAPIService from "../../../../services/api/user";
import { useUserManager } from "../../../../services/auth";
import { RESPONSE_ERROR_PATH } from "./contants";
import { IErrorDetail } from "../../../../utils/common/types";
import { RESPONSE_ERROR_KEY } from "../../../../utils/common/contants";

import "./styles.scss";

export const ChangePasswordModal: FC<IChangePasswordProps> = ({
  children,
  className,
  isOpen,
  user,
  onDismiss,
  isLoading,
  ...props
}) => {
  const userManager = useUserManager();
  const { t } = useTranslation();
  const [errors, setErrors] = useState<string[]>([]);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [isOldPasswordValid, setIsOldPasswordValid] = useState(true);
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(true);
  const [isConfirmNewPasswordValid, setIsConfirmNewPasswordValid] = useState(
    true,
  );
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const isFormValid = oldPassword && newPassword && confirmNewPassword;
  const passwordSettings = {
    minLength: 9,
    pattern: "[^ ]{8,}",
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (newPassword !== confirmNewPassword) {
      setNewPassword("");
      setOldPassword("");
      setConfirmNewPassword("");
      setIsNewPasswordValid(false);
      setIsConfirmNewPasswordValid(false);
      setErrors(["NewPasswordMismatch"]);
      return;
    }

    if (newPassword === oldPassword) {
      setNewPassword("");
      setConfirmNewPassword("");
      setIsNewPasswordValid(false);
      setIsConfirmNewPasswordValid(false);
      setErrors(["NewAndOldPasswordMatch"]);
      return;
    }

    setErrors([]);
    try {
      setIsFetching(true);
      await userAPIService.update({
        name: user.name,
        password: newPassword,
        currentPassword: oldPassword,
      });
      userManager.signoutRedirect();
    } catch (err) {
      if (!err.response) {
        setIsOldPasswordValid(false);
        setErrors([RESPONSE_ERROR_KEY.Illegal]);
      }

      if (err?.response?.status === 422) {
        const receivedErrors: string[] = [];

        err.response.data?.details?.map((elem: IErrorDetail) => {
          if (elem?.path === RESPONSE_ERROR_PATH.Password) {
            setIsNewPasswordValid(false);
            setIsConfirmNewPasswordValid(false);
          }
          if (elem?.path === RESPONSE_ERROR_PATH.CurrentPassword) {
            setIsOldPasswordValid(false);
          }

          setNewPassword("");
          setOldPassword("");
          setConfirmNewPassword("");
          receivedErrors.push(elem.problem);
        });
        setErrors(receivedErrors);
      }
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <Modal
      id="change-password-modal"
      className={classNames("ams-change-password-modal", className)}
      isOpen={isOpen}
      {...props}
    >
      <ModalHeader>{t("changePassword")}</ModalHeader>
      <ModalContent>
        {!!errors.length && (
          <div className="ams-change-password-modal__password-alert">
            <Alert
              className="ams-change-password-modal__error-alert"
              variant="error"
              content={
                <ul className="ams-change-password-modal__error-list">
                  {errors.map((error) => {
                    return <li key={error}>{t(`error.${error}`)}</li>;
                  })}
                </ul>
              }
            />
          </div>
        )}
        <div className="ams-change-password-modal__password-alert">
          <Alert
            variant="warning"
            content={
              <ul className="ams-change-password-modal__warning-list">
                <li>{t("passwordUppercaseLetter")}</li>
                <li>{t("passwordLowercaseLetter")}</li>
                <li>{t("passwordNumber")}</li>
                <li>{t("passwordSymbols")}</li>
              </ul>
            }
          >
            {t("passwordMustContain")}
          </Alert>
        </div>
        <form
          className="edit-profile-form"
          onSubmit={handleSubmit}
          id="change-password-form"
        >
          <FormGroup>
            <Label>{t("currentPassword")}</Label>
            <PasswordInput
              id="current-password-input"
              value={oldPassword}
              onChange={createInputChangeEventHandler(
                setOldPassword,
                setIsOldPasswordValid,
              )}
              error={!isOldPasswordValid}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("newPassword")}</Label>
            <PasswordInput
              id="new-password-input"
              value={newPassword}
              onChange={createInputChangeEventHandler(
                setNewPassword,
                setIsNewPasswordValid,
              )}
              minLength={passwordSettings.minLength}
              pattern={passwordSettings.pattern}
              error={!isNewPasswordValid}
              required
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("confirmNewPassword")}</Label>
            <PasswordInput
              id="new-confirm-password-input"
              value={confirmNewPassword}
              onChange={createInputChangeEventHandler(
                setConfirmNewPassword,
                setIsConfirmNewPasswordValid,
              )}
              error={!isConfirmNewPasswordValid}
              required
            />
          </FormGroup>
        </form>
      </ModalContent>
      <ModalFooter>
        <Button
          onClick={onDismiss}
          className="ams-modal__action"
          id="cancel-button"
        >
          {t("cancel")}
        </Button>
        <ButtonPrimary
          onClick={handleSubmit}
          className="ams-modal__action"
          id="submit-button"
          disabled={!isFormValid}
          isLoading={isFetching}
        >
          {t("saveChanges")}
        </ButtonPrimary>
      </ModalFooter>
    </Modal>
  );
};

function createInputChangeEventHandler(
  setValue: (value: string) => void,
  setValid: (error: boolean) => void,
) {
  return (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
    setValid(true);
  };
}
